import { Suspense } from 'react';
import Image from 'global/Image';

import dynamic from 'next/dynamic';
import { ErrorBoundary } from '@sentry/nextjs';
import ErrorFallback, { ErrorData } from 'global/ErrorBoundary/ErrorFallback';
import useDatafeedModalStore from './datafeed-modal.store';
import {
  CloseButton,
  CloseIcon,
  ModalHeader,
  Modal,
  AraArbSkeletons,
  SkeletonContainer,
  SkeletonPrice,
  SkeletonPercentage,
  SkeletonPercentageBase,
  ErrorWrapper,
} from './modal.styled';

interface IAraArbEstimationModal {
  isOpen: boolean;
  onClose: () => void;
}

const FallbackRenderer = (fallbackProps: ErrorData) => (
  <ErrorWrapper>
    <ErrorFallback errorData={fallbackProps} />
  </ErrorWrapper>
);

const AraArbSkeleton = () => (
  <AraArbSkeletons>
    {Array.from({ length: 15 }).map((_, index) => (
      <SkeletonContainer key={index.toLocaleString()}>
        <SkeletonPrice />
        <SkeletonPercentage />
        <SkeletonPercentageBase />
      </SkeletonContainer>
    ))}
  </AraArbSkeletons>
);

const AraArbRemote = dynamic(() => import('backpack/AraArb'), {
  ssr: false,
  loading: () => <AraArbSkeleton />,
});

export default function AraArbEstimationModal({
  isOpen,
  onClose,
}: Readonly<IAraArbEstimationModal>) {
  const modalData = useDatafeedModalStore((s) => s.modalData);
  const { symbol = '', data = [] } = modalData ?? {};

  return (
    <Modal
      title={null}
      closeIcon={<CloseIcon />}
      footer={null}
      visible={isOpen}
      onCancel={onClose}
      zIndex={1050}
      transitionName=""
    >
      <ModalHeader>
        <h1>ARA/ARB {symbol}</h1>
        <CloseButton onClick={onClose}>
          <Image src="/images/close-icon.svg" alt="" />
        </CloseButton>
      </ModalHeader>

      <ErrorBoundary fallback={FallbackRenderer}>
        <Suspense fallback={<AraArbSkeleton />}>
          <AraArbRemote themeMode="light" platform="web" data={data} />
        </Suspense>
      </ErrorBoundary>
    </Modal>
  );
}
